/**
 * Condition の評価。
 *
 * ネットワークに関する条件（canReach / canResolve / linkUp）は
 * 必ず sim に問い合わせる。判定結果をフラグとして保存しない。
 * そうすることで「ケーブルを抜けば目標が未達成に戻る」も自然に成立する。
 */

import type { NetworkWorld } from '../types/network';
import type { Condition, GameState } from '../types/scenario';
import { canReach, canReachPort, canResolve, getEffectiveIp, nslookup } from '../sim/diagnostics';
import { isLinkUp } from '../sim/topology';
import { allLinksUtilizationBelow, linkUtilizationPercent } from '../sim/traffic';
import { effectiveMbps } from '../sim/wireless';

/**
 * 参照先が見つからなかった条件を、1 種類につき 1 回だけ警告する。
 *
 * 【なぜ「1 回だけ」なのか】
 * effects.ts の deviceExists と同じく、宛先が居ない指定は黙って捨てたくない
 * （$device が置換されず章がクリア不能になった前例がある）。
 * ただし Condition は ScenarioEngine.tick() から**毎フレーム**評価されるので、
 * 素直に console.warn するとコンソールが埋まって他の警告が読めなくなる。
 * そこで「同じ指摘は 1 回だけ」に抑える。気づけることは変わらない。
 */
const WARNED = new Set<string>();

function warnOnce(key: string, message: string): void {
  if (WARNED.has(key)) return;
  WARNED.add(key);
  console.warn(message);
}

/** 警告の記憶を消す（章のやり直し・テスト用） */
export function clearConditionWarnings(): void {
  WARNED.clear();
}

/** 条件式を評価する。全バリアントに対応 */
export function evaluateCondition(
  condition: Condition | undefined,
  state: GameState,
  world: NetworkWorld,
): boolean {
  if (!condition) return true;

  switch (condition.type) {
    case 'always':
      return true;

    case 'flag': {
      const expected = condition.value ?? true;
      return (state.flags[condition.flag] ?? false) === expected;
    }

    case 'allFlags':
      return condition.flags.every((flag) => state.flags[flag] === true);

    case 'anyFlag':
      return condition.flags.some((flag) => state.flags[flag] === true);

    case 'not':
      return !evaluateCondition(condition.condition, state, world);

    case 'canReach':
      return canReach(world, condition.from, condition.to);

    case 'canResolve':
      return canResolve(world, condition.from, condition.hostname);

    // 「名前は引けるが、引けた番号が別物」を落とすのがこれ（章 3）。
    // canResolve は「何かの番号が引ける」ことしか見ないため、
    // でたらめな番号を登録しても真になってしまう。ここでは
    //   1. 名前解決そのものが成功していること
    //   2. 引けた番号が、その機器の「いま実際に使えている IP」と一致すること
    // の両方を要求する。機器の電源が落ちている／IP を持っていない
    // （APIPA を掴んでいる等）場合は、比べるべき現在の IP が無いので偽。
    case 'resolvesToDevice': {
      const lookup = nslookup(world, condition.from, condition.hostname);
      if (lookup.failureReason !== null || lookup.address === null) return false;

      const target = world.devices[condition.deviceId];
      if (!target || !target.powered) return false;

      const targetIp = getEffectiveIp(world, condition.deviceId);
      if (!targetIp || !targetIp.usable || targetIp.address === null) return false;

      return lookup.address === targetIp.address;
    }

    // 空配列は真。AND の単位元であり、「条件を書かなかった」＝
    // 制約なし（evaluateCondition(undefined) と同じ）に揃える。
    case 'allOf':
      return condition.conditions.every((c) => evaluateCondition(c, state, world));

    // 空配列は偽。OR の単位元であり、「満たしうる条件が 1 つも無い」＝
    // 永久に成立しない、として扱う（データの書き忘れが目標の達成に化けない）。
    case 'anyOf':
      return condition.conditions.some((c) => evaluateCondition(c, state, world));

    case 'linkUp':
      return isLinkUp(world, condition.linkId);

    case 'hasClue':
      return state.clues.includes(condition.clueId);

    // 「ping は通るのにアプリは開かない」を表すのがこれ（章 5）。
    // ICMP の疎通ではなく、指定した TCP ポートまで届くかを見る。
    case 'canReachPort':
      return canReachPort(world, condition.from, condition.to, condition.port);

    // 無線の実効速度。フラグ（電子レンジが回っているか等）で結果が変わる（章 4）
    case 'wirelessQualityAtLeast':
      return effectiveMbps(world, condition.apId, state.flags) >= condition.mbps;

    // 1 本のリンクの混み具合（章 6）。
    // 感染 PC を隔離するかポートを落とせば通信量が減り、ここが真に変わる。
    case 'linkUtilizationBelow': {
      const percent = linkUtilizationPercent(world, condition.linkId);
      if (percent === null) {
        warnOnce(
          `condition:linkUtilizationBelow:${condition.linkId}`,
          `[scenario] linkUtilizationBelow: ケーブル「${condition.linkId}」が見つかりません。この条件は成立しません`,
        );
        return false;
      }
      return percent < condition.percent;
    }

    // 全社が復旧したか（章 6 のクリア判定）。1 本でも混んでいれば偽
    case 'allLinksUtilizationBelow':
      return allLinksUtilizationBelow(world, condition.percent);

    default: {
      // 型が増えたときにコンパイルエラーで気づけるようにする
      const exhaustive: never = condition;
      console.warn('[scenario] 未知の Condition です', exhaustive);
      return false;
    }
  }
}

/** 条件を人間が読める日本語にする（デバッグ表示・手帳の目標表示の補助） */
export function describeCondition(condition: Condition): string {
  switch (condition.type) {
    case 'always':
      return '常に成立';
    case 'flag':
      return `フラグ ${condition.flag} が ${condition.value ?? true}`;
    case 'allFlags':
      return `${condition.flags.join(' / ')} がすべて成立`;
    case 'anyFlag':
      return `${condition.flags.join(' / ')} のいずれかが成立`;
    case 'not':
      return `${describeCondition(condition.condition)} でない`;
    case 'canReach':
      return `${condition.from} から ${condition.to} へ疎通する`;
    case 'canResolve':
      return `${condition.from} で ${condition.hostname} の名前解決ができる`;
    case 'resolvesToDevice':
      return `${condition.from} で ${condition.hostname} を引くと ${condition.deviceId} の現在の IP になる`;
    case 'allOf':
      return condition.conditions.length === 0
        ? '条件なし（allOf が空）'
        : `（${condition.conditions.map(describeCondition).join(' かつ ')}）`;
    case 'anyOf':
      return condition.conditions.length === 0
        ? '成立しない（anyOf が空）'
        : `（${condition.conditions.map(describeCondition).join(' または ')}）`;
    case 'linkUp':
      return `リンク ${condition.linkId} が上がっている`;
    case 'hasClue':
      return `手がかり ${condition.clueId} を入手済み`;
    case 'canReachPort':
      return `${condition.from} から ${condition.to} の ${condition.port} 番ポートへ接続できる`;
    case 'wirelessQualityAtLeast':
      return `${condition.apId} の実効速度が ${condition.mbps}Mbps 以上`;
    case 'linkUtilizationBelow':
      return `リンク ${condition.linkId} の使用率が ${condition.percent}% 未満`;
    case 'allLinksUtilizationBelow':
      return `すべてのリンクの使用率が ${condition.percent}% 未満`;
    default:
      return '不明な条件';
  }
}
